import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  Spinner,
  Button,
  VStack,
  HStack,
  IconButton,
  Popover,
  PopoverTrigger,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { AttachmentIcon, ChevronDownIcon, DownloadIcon, TimeIcon, ViewIcon } from "@chakra-ui/icons";
import { useFetch, baseUrl } from "../../../../hooks/useFetch";
import CustomToast from "../../../../components/CustomToast";
import { showDateTime } from "../../../../utilities/helper";
import { useAuth } from "../../../../hooks/useAuth";
import { FilterCard } from "../../../../components";
import { IFilterOptions } from "../../../../components/FilterCard/filterCard";
import { getStatusRank } from "../../LeadPool/model";
import CustomPagination from "../../../../components/CustomPagination";
import DateRangePicker from "../../../../components/DatePicker/datePicker";

interface ILeadAttachmentHistoryItem {
  id: number;
  leadId: number;
  description?: string;
  attachmentPath?: string;
  originalFileName?: string;
  createdBy?: number;
  createdByName?: string;
  createdAt: string;
  statusId?: number;
  status?: string;
}

interface Props {
  leadId?: number;
  refreshKey?: number;
}


const STATUS_ID_COLOR_MAP: Record<number, string> = {
  1: "blue",      // New Leads
  3: "yellow",    // Under Review
  4: "cyan",      // Assigned
  5: "purple",    // Requirement Gathering
  9: "teal",      // Contacted
  14: "orange",   // Negotiation
  15: "pink",     // Decision Pending
  16: "green",    // Won
  17: "red",      // Lost
  18: "gray",     // Not Interested
  19: "linkedin", // Reassigned
  20: "red",      // Invalid
  21: "purple", // Proposal Shared
  22: "blue",     // Follow-up
};

const STATUS_NAME_COLOR_MAP: Record<string, string> = {
  "new leads": "blue",
  "under review": "yellow",
  "assigned": "cyan",
  "requirement gathering": "purple",
  "contacted": "teal",
  "negotiation": "orange",
  "decision pending": "pink",
  "won": "green",
  "lost": "red",
  "not interested": "gray",
  "reassigned": "linkedin",
  "invalid": "red",
  "proposal shared": "facebook",
  "follow-up": "blue",
  "follow up": "blue",
};

const getStatusColor = (
  status?: string,
  statusId?: number
): string => {
  if (statusId && STATUS_ID_COLOR_MAP[statusId]) {
    return STATUS_ID_COLOR_MAP[statusId];
  }

  if (status) {
    const normalized = status.trim().toLowerCase();
    return STATUS_NAME_COLOR_MAP[normalized] ?? "gray";
  }

  return "gray";
};

const LeadAttachmentHistory = ({ leadId, refreshKey }: Props) => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const { loginObj } = useAuth();
  const [historyList, setHistoryList] = useState<ILeadAttachmentHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<IFilterOptions>({ filterOptions: {} });
  const [query, setQuery] = useState({
    filter: {},
    lazyParams: {
      first: 0,
      rows: 100,
      page: 0,
      sortField: "",
      sortOrder: 1,
    },
  });
  const [totalCount, setTotalCount] = useState(0);
 const [dateRange, setDateRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const {
  isOpen,
  onOpen,
  onClose,
} = useDisclosure();

const [selectedDescription, setSelectedDescription] = useState("");

    const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  const loadHistory = async () => {
    if (!leadId) return;
    setLoading(true);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const params = new URLSearchParams({
        lazyParams: JSON.stringify(query.lazyParams),
        filter: JSON.stringify(query.filter ?? {}),
        timeZone: timezone,
      });
  if (dateRange) {
      params.append("startDate", formatDate(dateRange.start));
      params.append("endDate", formatDate(dateRange.end));
    }
      const res = await fetchApi(
        `Lead/GetLeadAttachmentHistory/${leadId}`,
        "GET",
        null,
        params.toString(),
        ""
      );
      if (res) {
        setHistoryList(res.data ?? []);
        setTotalCount(res.totalRecords ?? 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFilter = async () => {
    const response = await fetchApi<IFilterOptions>("Filters/LeadAttachement", "GET", null, null, "");
    if (response) {
      if (response.filterOptions && response.filterOptions["Status"]) {
        response.filterOptions["Status"] = [...response.filterOptions["Status"]].sort((a: any, b: any) => {
          return getStatusRank(a.name) - getStatusRank(b.name);
        });
      }
      setFilter(response);
    }
  };

  useEffect(() => {
    loadFilter();
  }, []);

  useEffect(() => {
    loadHistory();
  }, [leadId, refreshKey, query.filter, query.lazyParams,dateRange]);

  const handlePageClick = ({ selected }: { selected: number }) => {
    const first = selected * query.lazyParams.rows;
    setQuery((prev) => ({
      ...prev,
      lazyParams: {
        ...prev.lazyParams,
        page: selected,
        first: first,
      },
    }));
  };

    const handleDateRangeChange = (
      newRange: { start: Date; end: Date } | null
    ) => {
      setDateRange(prev => {
        if (prev && newRange &&
          prev.start.getTime() === newRange.start.getTime() &&
          prev.end.getTime() === newRange.end.getTime()) {
          return prev;
        }
        return newRange;
      });
    };

  const downloadAttachment = async (historyId: number, fileName?: string) => {
    try {
      const token = loginObj?.token || localStorage.getItem("token") || "";
      const response = await fetch(`${baseUrl}Lead/download-lead-attachment/${historyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        addToast({ message: "Failed to download attachment.", status: "error" });
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "attachment";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      addToast({ message: "Error downloading attachment.", status: "error" });
    }
  };

  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  return (
    <Box mt={6} p={5} bg="white" borderRadius="12px" border="1px solid #E4E7EC">
      {/* Header row: title + count on the left, Filter + Refresh grouped on the right */}
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Flex gap={2} align="center">
          <Text fontWeight="600" fontSize="md" color="gray.800">
            Description & Attachment History
          </Text>
          <Badge colorScheme="blue" borderRadius="full" px={2} py={0.5}>
            {historyList.length}
          </Badge>
        </Flex>

        <HStack spacing={2}>


            <Box cursor="pointer">
          
                        <DateRangePicker
                          // value={dateRange}
                          maxDate={new Date()}
                          onChange={handleDateRangeChange}
                          showAdvancedFilters={false}
                        // defaultCurrentWeek
          
                        />
                      </Box>
          <Popover placement="bottom-end" closeOnBlur>
            <PopoverTrigger>
              <Button
                rightIcon={<ChevronDownIcon />}
                bg="#F9FAFB"
                border="1px solid #D0D5DD"
                fontSize="14px"
                minW="140px"
                justifyContent="space-between"
              >
                Filter
              </Button>
            </PopoverTrigger>

            <FilterCard
              filter={filter}
              selectedFilters={query.filter}
              onApply={(filters) => {
                const _filter: Record<string, number[]> = {};

                Object.keys(filters).forEach((key) => {
                  if (filters[key].length > 0) {
                    _filter[key.replace(/ /g, "")] = filters[key];
                  }
                });

                setQuery((prev) => ({
                  ...prev,
                  lazyParams: { ...prev.lazyParams, page: 0, first: 0 },
                  filter: _filter,
                }));
              }}
            />
          </Popover>

          <Button size="sm" variant="ghost" colorScheme="blue" onClick={loadHistory} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </Flex>

      <Box maxH="300px" overflowY="auto">
        {loading && historyList.length === 0 ? (
          <Flex justify="center" align="center" py={6}>
            <Spinner size="md" color="blue.500" />
          </Flex>
        ) : historyList.length === 0 ? (
          <Text fontSize="sm" color="gray.500" fontStyle="italic" py={4} textAlign="center">
            No description or attachment history recorded yet.
          </Text>
        ) : (
          <VStack spacing={2} align="stretch">
            {historyList.map((item) => (
              <Box key={item.id} p={1} px={2}>
                <Box bg="white" border="1px solid #EAECF0" borderRadius="8px" p={3}>
                  <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
                    <Flex gap={2} align="center">
                      <Text fontSize="xs" fontWeight="600" color="gray.700">
                        {item.createdByName || "User"}
                      </Text>

                      {item.status && (
                        <Badge
                          colorScheme={getStatusColor(item.status, item.statusId)}
                          borderRadius="full"
                          px={2}
                          py={0.5}
                          fontSize="10px"
                          textTransform="capitalize"
                        >
                          {item.status}
                        </Badge>
                      )}
                    </Flex>

                    <Flex gap={1} align="center" color="gray.500">
                      <TimeIcon boxSize="11px" />
                      <Text fontSize="xs">{showDateTime(item.createdAt)}</Text>
                    </Flex>
                  </Flex>
{item.description && (
  <Flex align="start" gap={2}>
    <Box
      flex="1"
      fontSize="sm"
      color="gray.800"
      sx={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        "& p": { m: 0 },
      }}
      dangerouslySetInnerHTML={{ __html: item.description }}
    />

    {stripHtml(item.description).length > 150 && (
      <Tooltip label="View Full Description">
        <IconButton
          aria-label="View Description"
          icon={<ViewIcon />}
          size="xs"
          variant="ghost"
          onClick={() => {
            setSelectedDescription(item.description ?? "");
            onOpen();
          }}
        />
      </Tooltip>
    )}
  </Flex>
)}       {item.attachmentPath && (
                    <Flex align="center" justify="space-between">
                      <Flex gap={2} align="center" overflow="hidden">
                        <AttachmentIcon color="blue.500" boxSize="14px" />
                        <Text fontSize="xs" fontWeight="500" color="blue.700" isTruncated maxW="300px">
                          {item.originalFileName || item.attachmentPath.split("/").pop()}
                        </Text>
                      </Flex>

                      <IconButton
                        aria-label="Download attachment"
                        icon={<DownloadIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        borderRadius="full"
                        onClick={() =>
                          downloadAttachment(item.id, item.originalFileName || item.attachmentPath?.split("/").pop())
                        }
                      />
                    </Flex>
                  )}
                </Box>
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      <CustomPagination
        pageCount={Math.ceil(totalCount / query.lazyParams.rows)}
        handlePageClick={handlePageClick}
        rowsPerPage={query.lazyParams.rows}
        rowsPerPageOptions={[50, 100, 200, 500]}
        setRowsPerPage={(value: number) =>
          setQuery((prev) => ({
            ...prev,
            lazyParams: {
              ...prev.lazyParams,
              rows: value,
              page: 0,
              first: 0,
            },
          }))
        }
        totalRecords={totalCount}
        currentPage={query.lazyParams.page}
      />
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
  <ModalOverlay />

  <ModalContent maxW="700px">
    <ModalHeader>Full Description</ModalHeader>
    <ModalCloseButton />

  <ModalBody
  maxH="400px"
  overflowY="auto"
  wordBreak="break-word"
>
  <Box
    sx={{
      "& p": { mb: 3 },
      "& ul, & ol": {
        pl: 6,
        mb: 3,
      },
      "& img": {
        maxWidth: "100%",
        height: "auto",
      },
      "& table": {
        width: "100%",
        borderCollapse: "collapse",
      },
      "& a": {
        color: "blue.500",
      },
    }}
    dangerouslySetInnerHTML={{ __html: selectedDescription }}
  />
</ModalBody>

    <ModalFooter>
      <Button size="sm" onClick={onClose}>
        Close
      </Button>
    </ModalFooter>
  </ModalContent>
</Modal>
    </Box>


  );
};

export default LeadAttachmentHistory;
